/**
 * KbApprovalPanel.tsx — KB-11, the gate where a tender's knowledge is bought.
 *
 * A bundle proposes ~18 documents and roughly 1.6M input tokens of synthesis.
 * Omar ratified on 2026-08-12 that none of it is spent without a human seeing the
 * list first, so intake marks every narrative document `pending` and nothing
 * moves until this panel posts a decision.
 *
 * Three rules this component exists to honour, all of them product decisions
 * rather than implementation taste:
 *
 * 1. **The app proposes; the Admin vetoes.** Every pending document starts
 *    CHECKED. Handing someone an empty list to fill in is how the useful default
 *    gets lost — they would tick the two they recognise by filename and lose the
 *    sixteen they did not, which is the failure the classifier already solved.
 * 2. **Decide on what a file CONTAINS.** The kind, page count and the document's
 *    own opening lines are shown, because a filename like
 *    `23086-D-ED-G-DDP_00` is not a basis for spending money.
 * 3. **It never blocks the analysis.** This is a panel beside the documents, not
 *    a modal in front of them. The tender analysis runs regardless; the knowledge
 *    base is a separate concern with a separate budget.
 *
 * Costs in € are deliberately absent: Revo is billed a flat fee, so a euro figure
 * is noise to the customer and exposes our unit economics. Pages are the honest
 * proxy and the thing that actually predicts the bill.
 */
import { I } from "../../components/icons";
import { useCanApproveKb } from "../../lib/kbApi";
import { type TenderDocument } from "../../lib/tenderApi";
import { pagesLabel, plural, recapLines, useKbDecision, useKbReAdd } from "../../lib/useKbDecision";
import { KbReceipt, receiptColor } from "./KbReceipt";
import { KbStatePill } from "./IntakeReviewPanel";

/**
 * Gate B, 2026-09-28 — the server's sentence for a document that did not go.
 *
 * Rendered on every row this panel lists, so a machine refusal never looks like
 * a person's choice: the Capitolato sat under «Esclusi» with nothing to say it
 * was too large to send, beside the line «Escluderli non è definitivo».
 */
function ForwardNote({ doc }: { doc: TenderDocument }): JSX.Element | null {
  if (!doc.kbForwardNote) return null;
  return (
    <div
      data-testid="kb-forward-note"
      style={{ fontSize: 11, color: "var(--c-err)", marginTop: 2 }}
    >
      {doc.kbForwardNote}
    </div>
  );
}

export function KbApprovalPanel({
  documents,
}: {
  documents: TenderDocument[];
}): JSX.Element | null {
  const isAdmin = useCanApproveKb();
  // Every mechanism that used to live here — the inverted `unchecked` set, the
  // synchronous double-click latch, the NULL-vs-zero page branch, the "ignored is
  // never swallowed" report — now lives in `useKbDecision`, shared with the
  // intake review on UploadView. See that file for why each one exists.
  const kb = useKbDecision(documents, null);
  /* `KB-19` — the other half of the decision: what was left OUT, and the way
     back in. A separate hook because its DEFAULT is the opposite one (nothing
     selected), for a reason written where it lives. */
  const reAdd = useKbReAdd(documents, null);
  const {
    optional: pending,
    lockedIn,
    approve,
    summary,
    isChecked,
    toggle,
    submit,
    isPending,
    done,
    sentLockedIn,
    notSent,
    receiptTone,
    error,
  } = kb;

  // Nothing proposed and nothing ever decided: this tender has no KB story yet,
  // and an empty panel would just be furniture.
  if (!kb.hasAnything) return null;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <I.Network size={15} />
        <div style={{ fontWeight: 600, fontSize: 14 }}>Knowledge Base</div>
      </div>

      {pending.length + lockedIn.length > 0 ? (
        <>
          {/* Counts BOTH buckets. Printing `pending.length` alone produced
              "0 documenti sono proposti per la sintesi" above a live
              "Conferma (1)" button with no checkboxes on screen — the blurb
              describing the vetoable set while the button sends the vetoable set
              PLUS the locked-in one. Common whenever the Relazione is a bundle's
              only KB document. */}
          <div style={{ fontSize: 12.5, color: "var(--c-ink-3)", marginBottom: 12 }}>
            {pending.length + lockedIn.length}{" "}
            {plural(
              pending.length + lockedIn.length,
              "documento è proposto",
              "documenti sono proposti",
            )}{" "}
            per la sintesi.{" "}
            {pending.length > 0 && "Togli la spunta a quelli che non vuoi includere. "}
            {!isAdmin && <strong>Solo un amministratore può confermare questa scelta.</strong>}
          </div>

          {/* Required-by-the-analysis documents that are also KB material — today
              only the Relazione generale. Omar, 2026-08-14: no checkbox, always
              included. Listed rather than hidden, so the confirm count adds up on
              screen instead of being one higher than the boxes the user can see. */}
          {lockedIn.length > 0 && (
            <div
              style={{
                fontSize: 11.5,
                color: "var(--c-ink-3)",
                background: "var(--c-bg-elev)",
                borderRadius: 8,
                padding: "8px 10px",
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 2 }}>
                Sempre {plural(lockedIn.length, "incluso", "inclusi")}, senza casella:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {lockedIn.map((d) => (
                  <li key={d.id}>
                    {d.labelIt || d.docKind} — <span className="t-mono">{d.filename}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 12 }}>
            {pending.map((d) => {
              const checked = isChecked(d.id);
              const pages = pagesLabel(d.pageCount);
              const lines = recapLines(d.titleBlock);
              return (
                <label
                  key={d.id}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 8,
                    cursor: isAdmin ? "pointer" : "default",
                    background: checked ? "var(--c-bg-elev)" : "transparent",
                    opacity: checked ? 1 : 0.55,
                    alignItems: "flex-start",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!isAdmin}
                    onChange={() => toggle(d.id)}
                    aria-label={d.filename}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13 }}>{d.filename}</div>
                    <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 1 }}>
                      {[d.labelIt || d.docKind, pages].filter(Boolean).join(" · ")}
                      {/* The third screen that can say «da confermare», and it
                          gets the reason for the same purpose: this is where an
                          Admin decides to spend on synthesising a document, so
                          "we are not sure what this is" is worth a why. */}
                      {d.needsReview && (
                        <span style={{ color: "var(--c-warn)" }}>
                          {" · tipo da confermare"}
                          {d.reviewReasonIt ? ` (${d.reviewReasonIt})` : ""}
                        </span>
                      )}
                    </div>
                    <ForwardNote doc={d} />
                    {lines.length > 0 && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--c-ink-3)",
                          marginTop: 4,
                          lineHeight: 1.45,
                          borderLeft: "2px solid var(--c-line)",
                          paddingLeft: 8,
                        }}
                      >
                        {lines.map((l, i) => (
                          <div
                            key={i}
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {l}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {isAdmin && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                className="btn btn-primary"
                onClick={() => void submit()}
                disabled={isPending}
              >
                {isPending ? "Invio…" : `Conferma (${approve.length})`}
              </button>
              <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>{summary}</div>
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 12.5, color: "var(--c-ink-3)" }}>
          Nessun documento in attesa di conferma.
        </div>
      )}

      {/* `KB-19` — WHICH documents, not how many of them.
          Omar, 2026-09-02: *"Documents that have already been added to the
          Knowledge Base are clearly identified and can no longer be
          selected/edited for this purpose."*

          This replaces a single grey line reading «12 in Knowledge Base · 3
          esclusi · 0 in corso», which named not one document. A count answers
          "did anything happen"; it cannot answer "is the Capitolato in there",
          which is the only question anyone actually brings to this page. */}
      {reAdd.locked.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
            Già decisi ({reAdd.locked.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {reAdd.locked.map((d) => (
              <div
                key={d.id}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "baseline",
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "var(--c-bg-elev)",
                }}
              >
                <div style={{ minWidth: 0, flex: 1, fontSize: 13 }}>
                  {d.filename}
                  <ForwardNote doc={d} />
                </div>
                <KbStatePill state={d.kbForwardState} />
              </div>
            ))}
          </div>
          {/* Said once, in words, rather than left to be inferred from the
              absence of a checkbox. A control that is simply missing teaches
              nothing — the reader cannot tell "you may not" from "we forgot". */}
          <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 6 }}>
            Un documento già inviato alla Knowledge Base non può essere tolto: la sintesi è già
            stata pagata.
          </div>
        </div>
      )}

      {/* `KB-19` — the door back in.
          Omar: *"Documents that were left out of the Knowledge Base remain
          available with a checkbox. The user can later select one or more of
          those PDFs and start the transformation process."*

          Nothing starts ticked. Its sibling above starts everything ticked
          because there the app proposes and a human vetoes; here the human has
          already said no to these, and one click re-approving a set of earlier
          refusals is not a default anyone asked for. See `useKbReAdd`. */}
      {reAdd.excluded.length > 0 && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--c-line)", paddingTop: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 2 }}>
            Esclusi dalla Knowledge Base ({reAdd.excluded.length})
          </div>
          <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginBottom: 10 }}>
            Escluderli non è definitivo. Seleziona quelli che vuoi aggiungere adesso.
            {!isAdmin && (
              <>
                {" "}
                <strong>Solo un amministratore può aggiungerli.</strong>
              </>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 12 }}>
            {reAdd.excluded.map((d) => {
              const picked = reAdd.isSelected(d.id);
              const pages = pagesLabel(d.pageCount);
              return (
                <label
                  key={d.id}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 8,
                    cursor: isAdmin ? "pointer" : "default",
                    background: picked ? "var(--c-bg-elev)" : "transparent",
                    alignItems: "flex-start",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={picked}
                    disabled={!isAdmin || reAdd.isPending}
                    onChange={() => reAdd.toggle(d.id)}
                    aria-label={d.filename}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13 }}>{d.filename}</div>
                    <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 1 }}>
                      {[d.labelIt || d.docKind, pages].filter(Boolean).join(" · ")}
                    </div>
                    <ForwardNote doc={d} />
                  </div>
                </label>
              );
            })}
          </div>
          {isAdmin && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                className="btn btn-primary"
                onClick={() => void reAdd.submit()}
                disabled={reAdd.isPending || reAdd.selected.length === 0}
              >
                {reAdd.isPending
                  ? "Invio…"
                  : `Aggiungi alla Knowledge Base (${reAdd.selected.length})`}
              </button>
              {/* What it costs, before it is spent. The button commits real
                  synthesis, and the page count is the honest proxy this
                  product already uses for that (see the header of this file). */}
              {reAdd.summary && (
                <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>{reAdd.summary}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Gate B — OUTSIDE the «Esclusi» block, deliberately. The receipt used to
          live inside it, so a re-add that emptied the list unmounted the only
          sentence saying what had happened; and it must stay on screen while the
          document it describes moves to «Già decisi» and the list polls. */}
      {reAdd.done && (
        <div
          data-testid="kb-readd-receipt"
          style={{ fontSize: 12, color: receiptColor(reAdd.receiptTone), marginTop: 10 }}
        >
          <KbReceipt decision={{ done: reAdd.done, sentLockedIn: [], notSent: reAdd.notSent }} />
        </div>
      )}
      {reAdd.error && (
        <div style={{ fontSize: 12, color: "var(--c-err)", marginTop: 10 }}>{reAdd.error}</div>
      )}

      {done && (
        <div style={{ fontSize: 12, color: receiptColor(receiptTone), marginTop: 10 }}>
          <KbReceipt decision={{ done, sentLockedIn, notSent }} />
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: "var(--c-err)", marginTop: 10 }}>{error}</div>}
    </div>
  );
}
